import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { usePageProps, useQueryUpdater } from '@/hooks';

import {
  ActiveSortTypeLabel,
  Option,
  OptionInput,
  OptionLabel,
  Options,
  OptionsWrapper,
  StyledIcon,
  ToggleBtn,
  Wrapper,
} from './SortSelect.styles';

const DEFAULT_SORT = 'popular';

function SortSelect({ options }) {
  const { formatMessage } = useIntl();
  const [showOptions, toggleOptionsList] = useState(false);
  const { params } = usePageProps();
  const { setQueryParam } = useQueryUpdater();
  const wrapperRef = useRef();
  const value = params?.sort || DEFAULT_SORT;

  const activeSortTypeLabel = useMemo(() => {
    const activeSortType = options.find((optionValue) => optionValue === value);

    return formatMessage({
      id: `sort.${activeSortType}`,
    });
  }, [options, value, formatMessage]);

  const handleChange = (event) => {
    const { value: newValue } = event.target;

    setQueryParam({
      key: 'sort',
      value: newValue !== DEFAULT_SORT ? newValue : undefined,
    });
  };

  const handleToggleBtnClick = () => {
    toggleOptionsList(!showOptions);
  };

  const handleOptionsClick = () => {
    toggleOptionsList(false);
  };

  const handleDocumentClick = (event) => {
    const targetElement = event.target;

    if (
      !wrapperRef.current.contains(targetElement) &&
      wrapperRef.current !== targetElement
    ) {
      toggleOptionsList(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return (
    <Wrapper ref={wrapperRef}>
      <ToggleBtn onClick={handleToggleBtnClick}>
        <StyledIcon name="sort" />

        <ActiveSortTypeLabel>{activeSortTypeLabel}</ActiveSortTypeLabel>
      </ToggleBtn>

      <OptionsWrapper $showOptions={showOptions} onClick={handleOptionsClick}>
        <Options>
          {options.map((option, index) => (
            <Option htmlFor={`sort-option-${index}`} key={option}>
              <OptionInput
                id={`sort-option-${index}`}
                type="radio"
                value={option}
                checked={value === option}
                onChange={handleChange}
              />

              <OptionLabel>
                {formatMessage({
                  id: `sort.${option}`,
                })}
              </OptionLabel>
            </Option>
          ))}
        </Options>
      </OptionsWrapper>
    </Wrapper>
  );
}

SortSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
};

export default memo(SortSelect);
